import '../css/markdown.css';
import React, {useEffect, useState, useRef} from 'react';
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import Markdown from 'react-markdown';
import {Form, Formik} from "formik";
import {
    Alert,
    Button,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Stack,
    TextField,
    Typography,
    Box,
    Divider,
    CircularProgress
} from "@mui/material";
import Paper from "@mui/material/Paper";
import {signin, logout, onLogout} from "../serverREST";
import {setOpenTooManySessions} from "../redux/licenseSlice";
import theme from '../css/appTheme.js';
import yaml from 'js-yaml';
import {Helmet} from 'react-helmet-async';
import LanguageSwitcher from "../components/LanguageSwitcher";
import {useTranslation} from "react-i18next";

function LoginForm({paperRef}) {
    const { t } = useTranslation();
    const collapseLogInAlertCode = useSelector(state => state.lists.collapseLogInAlertCode);
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    const hasAlert = collapseLogInAlertCode !== 0;

    const getAlertMessage = () => {
        switch (collapseLogInAlertCode) {
            case 418:
            case 429:
                return 'Превышен лимит пользователей';
            case 401:
                return 'Неверный логин или пароль';
            case 403:
                return 'Отказ в доступе, пожалуйста, повторите вход';
            case 417:
                return 'Действие учетной записи временно приостановлено, пожалуйста, обратитесь в Поддержку';
            case -1:
                return 'Сервер недоступен';
            case 502:
                return `Сервер недоступен (${collapseLogInAlertCode})`;
            case 0:
                return '';
            default:
                return `Неизвестная ошибка (${collapseLogInAlertCode})`;
        }
    };

    useEffect(() => {
        if (location.state?.fromExitButton) {
            onLogout();
            logout();
        }
    }, [location]);
    return (
        <Paper ref={paperRef} sx={{m: 1, p: 2, zIndex: 2,}} elevation={2}>
            <Formik
                initialValues={{
                    login: '',
                    password: '',
                }}

                onSubmit={
                    (values) => {
                        signin(values.login, values.password, setLoading)
                    }
                }>
                {({setFieldValue, values}) => (
                    <Form>
                        <Typography align={'center'} variant={'h4'}>
                            {/* <Link href='/tariffs/motordata-normy-tariff/' color="inherit" underline="hover">
                                Мотордата
                            </Link>&#32;Нормы времени
                            */}
                            <Link href='/tariffs/motordata-normy-tariff/' color="inherit" underline="hover">{t('header_text')}</Link>
                        </Typography>

                        <Typography align={'center'} sx={{mt: 1, mb: 2}} variant={'body2'}>
                            Еще не зарегистрированы? <a href={'/cab/ru/sign/in'}>Создать аккаунт</a>
                        </Typography>

                        <TextField label="Логин"
                                   variant="outlined"
                                   value={values.login}
                                   sx={{mt: 1}}
                                   autoComplete="username"
                                   onChange={
                                       (event) => {
                                           setFieldValue('login', event.target.value)
                                       }}
                                   fullWidth/>

                        <TextField label="Пароль"
                                   type={'password'}
                                   variant="outlined"
                                   value={values.password}
                                   autoComplete="current-password"
                                   sx={{mt: 1}}
                                   onChange={
                                       (event) => {
                                           setFieldValue('password', event.target.value)
                                       }}
                                   fullWidth/>

                        <Collapse in={hasAlert}>
                            <Alert sx={{mt: 1}} severity="error">{getAlertMessage()}</Alert>
                        </Collapse>

                        <Button
                            variant={'contained'}
                            fullWidth
                            sx={{mt: 1}}
                            type={'submit'}
                            disabled={loading}
                        >
                            {loading ?
                                <CircularProgress color="inherit" size={30}/>
                                : <Typography variant='subtitle2'>Войти</Typography>
                            }
                        </Button>

                        <Typography align={'center'} sx={{mt: 1}}>
                            <Button onClick={() => {
                                // navigate('../../chpass')
                            }}>
                                <Link href={'/cab/ru/chpass'} underline={'none'} color="inherit">
                                    Забыли пароль?
                                </Link>
                            </Button>
                        </Typography>

                        <Typography align={'center'} variant={'body2'}>
                            <a href={'/'}>Вернуться на главную</a>
                        </Typography>
                    </Form>
                )}
            </Formik>
        </Paper>
    );
}

function UsersLimitDialog({open, onClose}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
        >
            <DialogTitle>
                Лимит пользователей
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Вы достигли лимита количества одновременных пользователей.
                    Для использования аккаунта на большем числе устройств одновременно предлагаем Вам
                    приобрести тариф с расширенным лимитом.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button>
                    <Link href={'../purchase/tariffs'} underline={'none'} color="inherit">
                        Посмотреть лицензии
                    </Link>
                </Button>
                <Button onClick={onClose}>
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function Footer({footerRef, visible, children}) {
    const [footerHeight, setFooterHeight] = useState(85);

    useEffect(() => {
        if (footerRef.current) {
            setFooterHeight(footerRef.current.clientHeight);
        }
    }, []);

    return (
        <Stack direction={'row'} justifyContent='center'>
            {visible
                ?
                <Stack
                    ref={footerRef}
                    direction={'column'}
                    sx={{position: 'fixed', bottom: 0, backgroundColor: 'white', zIndex: 1}}
                >
                    <Divider/>
                    {children}
                </Stack>
                :
                <Box
                    ref={footerRef}
                    sx={{position: 'fixed', bottom: 0, height: `${footerHeight}px`, width: 50, zIndex: 1}}
                />
            }
        </Stack>
    );
}

const DividerWithText = ({text}) => {
    return (
        <Stack direction="row" alignItems="center" spacing={2} sx={{pl: 2, pr: 2, mt: 1, mb: 1}}>
            <Divider sx={{flexGrow: 1}}/>
            <Typography variant="body2" color="textSecondary">
                {text}
            </Typography>
            <Divider sx={{flexGrow: 1}}/>
        </Stack>
    );
};

async function loadNews(callback) {
    const publicUrl = process.env.PUBLIC_URL;
    const newsUrl = `${publicUrl}/norms-news.yaml?v=${Date.now()}`;

    try {
        const response = await fetch(newsUrl);
        const yamlText = await response.text();
        const data = yaml.load(yamlText);

        if (!data) {
            //console.log('No news');
        } else {
            if (!Array.isArray(data)) {
                throw new Error('Data is not an array.');
            }
            const isValidData = data.every((item) => {
                return item
                    && typeof item === 'object'
                    && 'header' in item && typeof item.header === 'string'
                    && 'text' in item && typeof item.text === 'string'
                    ;
            });
            if (!isValidData) {
                throw new Error('Invalid data.');
            }

            callback && callback(data);
        }
    } catch (error) {
        console.error('News URL:', newsUrl);

        if (error instanceof yaml.YAMLException) {
            console.error("News don't loaded: YAMLException reason:", error.reason);
        } else {
            console.error("News don't loaded:", error);
        }
    }
}

function NewsList({}) {
    const [news, setNews] = useState([]);

    useEffect(() => {
        loadNews((data) => setNews(data));
    }, []);

    if (!news || news.length === 0) {
        return (<React.Fragment/>);
    }
    return (
        <React.Fragment>
            <DividerWithText text='Новости'/>
            <Stack direction={'column'} sx={{m: 1, mt: 0}}>
                {news.map((newsData, index) => (
                    <Paper
                        key={index}
                        variant='outlined'
                        sx={{mt: 1, p: 2, borderColor: theme.palette.primary.main}}
                    >
                        <Typography sx={{m: 1, mt: 0, ml: 0}} variant={'body1'} fontWeight="bold">
                            {newsData.header}
                        </Typography>
                        <div style={{...theme.typography.body2}}>
                            <Markdown className={'markdown'}>{newsData.text}</Markdown>
                        </div>
                    </Paper>
                ))}
            </Stack>
        </React.Fragment>
    );
}

export default function Login() {
    const [footerHeight, setFooterHeight] = useState(85);
    const [isFooterVisible, setIsSecondStackVisible] = useState(true);

    const paperRef = useRef(null);
    const footerRef = useRef(null);

    const loggedIn = useSelector(state => state.lists.loggedIn);
    const openSessionsAlert = useSelector(state => state.license.openSessionsAlert);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/repair-times";

    function signIn() {
        navigate(from, {replace: true});
    }

    const handleClose = () => {
        dispatch(setOpenTooManySessions(false))
    };

    useEffect(() => {
        if (loggedIn) {
            signIn()
        }
    })

    useEffect(() => {
        if (footerRef.current) {
            setFooterHeight(footerRef.current.clientHeight);
        }
    }, []);

    useEffect(() => {
        if (!paperRef.current || !footerRef.current) {
            return;
        }
        const checkOverlap = () => {
            const paperRect = paperRef.current.getBoundingClientRect();
            const secondStackRect = footerRef.current.getBoundingClientRect();
            const isOverlap = !(
                paperRect.right < secondStackRect.left ||
                paperRect.left > secondStackRect.right ||
                paperRect.top > secondStackRect.bottom ||
                secondStackRect.top > paperRect.bottom
            );
            setIsSecondStackVisible(!isOverlap);
        };
        checkOverlap();

        window.addEventListener('resize', checkOverlap);
        window.addEventListener('scroll', checkOverlap);
        return () => {
            window.removeEventListener('resize', checkOverlap);
            window.removeEventListener('scroll', checkOverlap);
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Вход в Мотордата Нормы Времени</title>
                <meta
                    name="description"
                    content="Страница входа и регистрации программы Мотордата Нормы Времени"
                    key="description"
                />
            </Helmet>
            <Stack display={"flex"} gap={1} flexDirection={"row"} alignItems={"center"} justifyContent={"end"} marginRight={20}>
                <Stack marginTop={1}>
                    <LanguageSwitcher color={'#035506'}/>
                </Stack>
            </Stack>
            <Box>
                <Container maxWidth={'sm'}>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        sx={{
                            position: 'relative',
                            minHeight: `95vh`,
                            overflowY: 'auto',
                        }}
                    >
                        <LoginForm paperRef={paperRef}/>
                        <NewsList/>
                        <Box sx={{height: `${footerHeight}px`}}/>
                    </Stack>
                    <UsersLimitDialog open={openSessionsAlert} onClose={handleClose}/>
                </Container>
                <Footer footerRef={footerRef} visible={isFooterVisible}>
                    <Typography align={'center'} variant="caption" sx={{mt: 1}}>
                        Content is not yet available in your language. We appologize for the inconvenience
                    </Typography>
                    <Typography align={'center'} variant="caption" sx={{mt: 1}}>
                        El contenido aún no está disponible en su idioma. Pedimos disculpas por las molestias.
                    </Typography>
                    <Typography align={'center'} variant="caption" sx={{mt: 1}}>
                        Le contenu n'est pas encore disponible dans votre langue.Nous nous excusons pour le dérangement.
                    </Typography>
                </Footer>
            </Box>
        </>
    );
}